import React from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import AppButton from "../../components/ui/AppButton";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMCampusList = ({
  campuses,
  loading,
  totalElements,
  searchTerm,
  onSearchChange,
  onClearSearch,
  onCreate,
  onEdit,
  onDelete,
  onReactivate,
  statusFilter,
  onStatusFilterChange,
}) => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();

  const isDeleted = statusFilter === false;

  return (
    <div className="campus-list">
      <ListToolbar
        title={t("campuses.list.title")}
        subtitle={t("campuses.list.subtitle", { count: totalElements })}
        filterBadges={searchTerm ? <span className="badge bg-primary">Search: "{searchTerm}"</span> : null}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-medium">{t("campuses.list.searchLabel")}</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder={t("campuses.list.searchPlaceholder")}
                value={searchTerm}
                onChange={onSearchChange}
              />
              {searchTerm && (
                <button type="button" className="btn btn-outline-danger" onClick={onClearSearch} title={tCommon("buttons.clear")}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>

          <div className="col-md-6 d-flex align-items-end justify-content-md-end gap-2 flex-wrap">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === true ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => onStatusFilterChange(true)}
              >
                <i className="fas fa-check-circle me-1"></i>
                {t("campuses.list.filterActive")}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === false ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={() => onStatusFilterChange(false)}
              >
                <i className="fas fa-trash-alt me-1"></i>
                {t("campuses.list.filterDeleted")}
              </button>
            </div>
            {!isDeleted && can(PERMISSIONS.campus.create) && (
              <AppButton variant="primary" className="btn-sm" onClick={onCreate}>
                <i className="fas fa-plus me-1"></i>{t("crud.addCampus")}
              </AppButton>
            )}
          </div>
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{tCommon("actions.loading")}</span></div>
              <p className="text-muted mt-2">{t("campuses.list.loading")}</p>
            </div>
          ) : campuses.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-building text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">{t("campuses.list.noFound")}</h5>
              <p className="text-muted">{searchTerm ? t("campuses.list.adjustSearch") : t("campuses.list.noneCreated")}</p>
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4">{t("campuses.list.columns.campus")}</th>
                  <th className="text-center">{t("campuses.list.columns.students")}</th>
                  <th className="text-center">{t("campuses.list.columns.clubs")}</th>
                  <th className="text-center">{t("campuses.list.columns.events")}</th>
                  <th className="text-center pe-4">{t("crud.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {campuses.map((campus) => (
                  <tr key={campus.id} className="border-bottom">
                    <td className="ps-4">
                      <div className="fw-bold text-dark">{campus.nameLocal || campus.nameEn}</div>
                      <small className="text-muted">{campus.nameEn}</small>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-info bg-opacity-10 text-info fw-bold">
                        <i className="fas fa-user-graduate me-1"></i>
                        {campus.studentCount?.toLocaleString() || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-success bg-opacity-10 text-success fw-bold">
                        <i className="fas fa-users me-1"></i>
                        {campus.clubCount || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-primary bg-opacity-10 text-primary fw-bold">
                        <i className="fas fa-calendar-alt me-1"></i>
                        {campus.eventCount || 0}
                      </span>
                    </td>
                    <td className="text-center pe-4">
                      {isDeleted ? (
                        can(PERMISSIONS.campus.update) && (
                          <AppButton
                            variant="outline"
                            className="btn-sm text-success border-success"
                            onClick={() => onReactivate && onReactivate(campus)}
                            title={t("crud.reactivate")}
                          >
                            <i className="fas fa-undo me-1"></i>
                            {t("crud.reactivate")}
                          </AppButton>
                        )
                      ) : (
                        <div className="btn-group" role="group">
                          {can(PERMISSIONS.campus.update) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-primary border-primary"
                              onClick={() => onEdit && onEdit(campus)}
                              title={tCommon("buttons.edit")}
                            >
                              <i className="fas fa-edit"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.campus.delete) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-danger border-danger"
                              onClick={() => onDelete && onDelete(campus)}
                              title={tCommon("buttons.delete")}
                            >
                              <i className="fas fa-trash"></i>
                            </AppButton>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default UMCampusList;
